import React, { useRef, useEffect } from 'react';
import {
  Box,
  VStack,
  Card,
  CardBody,
  HStack,
  Spinner,
  Text,
  useColorModeValue
} from '@chakra-ui/react';
import ChatMessage, { ChatMessageProps } from './ChatMessage';
import ChatInput from './ChatInput';
import { colors } from '../../../assets/styles/colors';

export interface ChatInterfaceProps {
  messages: Omit<ChatMessageProps, 'onWordSelect'>[];
  currentMessage: string;
  setCurrentMessage: (message: string) => void;
  sendMessage: () => void;
  isAgentTyping: boolean;
  onWordSelect: (word: string) => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  currentMessage,
  setCurrentMessage,
  sendMessage,
  isAgentTyping,
  onWordSelect
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const cardBg = useColorModeValue('white', colors.primary[700]);
  const chatBg = useColorModeValue('gray.50', colors.primary[800]);
  const agentMessageBg = useColorModeValue('white', colors.primary[600]);
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.300');
  const accentColor = useColorModeValue('blue.500', 'blue.400');

  useEffect(() => {
    scrollToBottom();
  }, [messages, isAgentTyping]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Card bg={cardBg} shadow="2xl" borderRadius="3xl" minH="400px">
      <CardBody p={0}>
        <VStack spacing={0} h="500px">
          {/* Chat Messages */}
          <Box
            flex="1"
            w="full"
            bg={chatBg}
            borderTopRadius="3xl"
            p={6}
            overflowY="auto"
          >
            <VStack spacing={4} align="stretch">
              {messages.map((message) => (
                <ChatMessage
                  key={message.id}
                  {...message}
                  onWordSelect={onWordSelect}
                />
              ))}

              {isAgentTyping && (
                <HStack spacing={3} align="start">
                  <Box
                    bg={agentMessageBg}
                    color={textColor}
                    p={4}
                    borderRadius="2xl"
                    borderBottomLeftRadius="md"
                    ml={10} // Approximate avatar width + spacing
                  >
                    <HStack spacing={2}>
                      <Spinner size="sm" color={accentColor} />
                      <Text fontSize="sm" color={mutedTextColor}>
                        AI is thinking...
                      </Text>
                    </HStack>
                  </Box>
                </HStack>
              )}

              <div ref={messagesEndRef} />
            </VStack>
          </Box>

          {/* Chat Input */}
          <ChatInput
            value={currentMessage}
            onChange={setCurrentMessage}
            onSend={sendMessage}
            isDisabled={isAgentTyping}
            isLoading={isAgentTyping}
          />
        </VStack>
      </CardBody>
    </Card>
  );
};

export default ChatInterface;